// ════════════════════════════════════════════════════════════════════════
//  Die Begleitleiste
//
//  Sie sitzt im Rahmen, unter dem Kopf und über dem Inhalt, und geht bei einem
//  Seitenwechsel nicht verloren. Kein Overlay, kein eigener Assistent: die
//  Lehrkraft arbeitet die ganze Zeit in der WIRKLICHEN Anwendung. Sie sieht
//  nicht zu, wie eine Klasse angelegt wird - sie legt eine an.
//
//  ZWEI GESTALTEN VON KNOPF, UND DER UNTERSCHIED IST DIE DATENLAGE
//
//  Eine Station an echten Daten (`done !== null`) hat KEINEN Weiter-Knopf: sie
//  hakt sich ab, wenn die Sache vorliegt. Eine Station, die nur angesehen wird,
//  hat einen - denn sonst gäbe es keinen Weg weiter. Damit fällt die Frage
//  „gibt es hier einen Knopf?“ mit der Frage „ist das gemessen?“ zusammen, und
//  es kann nicht passieren, dass ein Knopf etwas abhakt, was nicht geschehen
//  ist.
//
//  „SPÄTER“ IST IMMER DA
//
//  Kein modaler Zwang, keine erzwungene Reihenfolge, kein Klick, der nicht
//  wegführt. Eine Führung, aus der man nicht herauskommt, ist eine Geiselnahme -
//  und die erste Lehrkraft, der das im Kollegium passiert, erzählt es weiter.
// ════════════════════════════════════════════════════════════════════════

import { Link, useLocation } from "react-router-dom";
import { useTour } from "../../../shared/context/TourContext.js";
import { appAddress } from "../../codes/lib/cards.js";
import { TOUR_WELCOME } from "../lib/tour.js";
import own from "./tour.module.css";
import shell from "../../../shared/components/shell.module.css";

/**
 * Die Adresse der Lern-App – dieselbe, die als QR auf den Kärtchen steht.
 *
 * Fehlt sie, entfällt der Link. Das ist derselbe Ausgang wie beim QR-Code: eine
 * Adresse, die auf „localhost“ zeigt, sähe richtig aus und führte in einer
 * Schule ins Nichts.
 */
function lernAppUrl(): string | null {
  return appAddress((import.meta.env as Record<string, string>)["VITE_LERN_APP_URL"]);
}

export function TourBar() {
  const tour = useTour();
  const { pathname } = useLocation();

  if (!tour || !tour.running) return null;

  const { step, position, route, checklist } = tour;
  const lernApp = step.key === "lern-app" ? lernAppUrl() : null;
  // Ein Link auf die Seite, auf der man steht, ist keiner - er sieht nur aus
  // wie ein Weg.
  const ziel = route !== null && route !== pathname ? route : null;
  /** Die letzte Station, und die Führung steht wirklich dort. */
  const ende = step.key === "fertig" && tour.isCurrent;

  return (
    // Die Leiste folgt der Seitenbreite: `page` und `breiteWeit` sind dieselben
    // Klassen, die `Page` an der Route benutzt. Sonst liefe sie auf einem
    // 27-Zöller über die ganze Breite, während die Karte darunter bei 1600px
    // aufhört - und der Bezug zum hervorgehobenen Knopf wäre weg.
    <section
      className={`${shell.page} ${shell.breiteWeit} ${own.bar}`}
      aria-label="Erste Schritte"
    >
      <p className={own.count}>
        Schritt {position.number} von {position.total}
      </p>
      {tour.atStart && <p className={own.welcome}>{TOUR_WELCOME}</p>}

      {/* ALLE Stationen, nicht nur die eine, die dran ist.
          Vorher zeigte die Leiste ausschließlich die laufende Station - und wer
          die Führung nach dem Abschluss wieder aufrief, landete für immer auf
          „Fertig“: ein Bildschirm, der behauptet, es sei nichts mehr zu sehen.
          Die Liste beantwortet beide Fragen auf einmal, „was habe ich schon
          gemacht“ und „was stand da noch mal“.

          Der Haken ist eine ANZEIGE, kein Kästchen zum Ankreuzen: erledigt wird
          eine Station durch die Sache selbst, nicht durch einen Klick. Ein echtes
          `<input type="checkbox">` wäre ein Versprechen, das die Führung nicht
          halten kann - und dürfte es, wäre die ganze Regel „die Stationen haken
          sich an den Daten ab“ hinfällig. */}
      <ol className={own.list}>
        {checklist.map((eintrag) => {
          const offen = eintrag.step.key === step.key;
          return (
            <li key={eintrag.step.key}>
              <button
                type="button"
                className={own.row}
                // Der Zustand gehört in den Namen: ein Haken, den nur das Auge
                // sieht, ist für eine Vorleseausgabe nicht da.
                aria-label={`${eintrag.step.title} – ${eintrag.done ? "erledigt" : "offen"}`}
                {...(offen ? { "aria-current": "step" as const } : {})}
                onClick={() => tour.select(eintrag.step.key)}
              >
                <span className={own.box} data-erledigt={eintrag.done} aria-hidden="true">
                  {eintrag.done ? "✓" : ""}
                </span>
                <span className={own.rowTitle}>
                  {eintrag.number}. {eintrag.step.title}
                </span>
              </button>

              {offen && <p className={own.text}>{step.text}</p>}
            </li>
          );
        })}
      </ol>

      <div className={own.actions}>
        {ziel !== null && (
          <Link to={ziel} className={own.linkButton}>
            Dorthin
          </Link>
        )}
        {lernApp !== null && (
          // Neues Fenster, damit die Führung nicht abbricht. Angeboten wird der
          // Gastmodus - ein echter Code der eigenen Klasse würde Übungsdaten
          // unter einem Kind erzeugen, das es nicht gibt.
          <a href={lernApp} target="_blank" rel="noreferrer" className={own.linkButton}>
            Lern-App öffnen
          </a>
        )}
        {/* Eine Station an echten Daten hakt sich selbst ab und hat deshalb
            keinen Knopf. Nur ein Blick braucht einen - sonst gäbe es keinen Weg
            weiter. */}
        {/* „Abschließen“ nur, wenn die letzte Station auch die laufende ist. Wer
            sie bloß nachliest, während er in der Mitte steht, bekäme sonst einen
            Knopf, der „fertig“ sagt und das Lesezeichen dort stehen lässt, wo es
            war - eine Beschriftung, die den eigenen Vorgang falsch benennt. */}
        {ende ? (
          <button type="button" className={shell.button} onClick={tour.close}>
            Führung abschließen
          </button>
        ) : (
          // Nur auf der laufenden Station: beim Nachlesen einer früheren würde
          // „weiter“ das Lesezeichen zurückziehen.
          step.done === null &&
          tour.isCurrent && (
            <button type="button" className={shell.button} onClick={tour.advance}>
              Angesehen, weiter
            </button>
          )
        )}
        {/* Auf der letzten Station wäre „Später“ neben „abschließen“ derselbe
            Vorgang zweimal: die Leiste geht in beiden Fällen weg. */}
        {!ende && (
          <button
            type="button"
            className={`${shell.buttonQuiet} ${own.later}`}
            onClick={tour.close}
          >
            Später
          </button>
        )}
      </div>
    </section>
  );
}
